import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { POLICIES, BUSINESS } from "@/lib/policies";
import { ArrowLeft, Building2, Globe, MapPin, Phone, Mail, IndianRupee, FileText } from "lucide-react";

function ContactCard() {
  const rows = [
    { icon: Building2, label: "Legal Business Name", value: BUSINESS.legalName },
    { icon: Globe, label: "Website", value: BUSINESS.website, href: BUSINESS.websiteUrl },
    { icon: MapPin, label: "Address", value: BUSINESS.address },
    { icon: Phone, label: "Mobile", value: BUSINESS.mobile, href: `tel:+91${BUSINESS.mobile}` },
    { icon: Mail, label: "Email", value: BUSINESS.email, href: `mailto:${BUSINESS.email}` },
  ];
  return (
    <div className="glass-strong rounded-2xl p-6 border border-amber-500/30 grid gap-4 sm:grid-cols-2" data-testid="contact-card" data-allow-copy>
      {rows.map((r) => (
        <div key={r.label} className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-xl grid place-items-center bg-amber-500/10 border border-amber-500/30 shrink-0"><r.icon size={16} className="text-amber-300" /></div>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">{r.label}</div>
            {r.href ? <a href={r.href} className="text-amber-100 hover:text-amber-300 break-all" target={r.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer">{r.value}</a> : <div className="text-amber-100">{r.value}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function ServiceCard({ lang }) {
  const [price, setPrice] = useState(null);
  useEffect(() => { api.get("/payment/config").then(({ data }) => setPrice(data.price_inr)).catch(() => {}); }, []);
  return (
    <div className="glass-strong rounded-2xl p-6 border border-amber-500/30 flex flex-col sm:flex-row sm:items-center gap-5" data-testid="service-card">
      <div className="h-14 w-14 rounded-2xl grid place-items-center bg-gradient-to-br from-amber-500/20 to-purple-600/20 border border-amber-500/30 shrink-0"><FileText className="text-amber-300" size={26} /></div>
      <div className="flex-1">
        <div className="font-serif text-xl text-amber-100">{lang === "hi" ? "ऑनलाइन हस्तरेखा रिपोर्ट" : "Online Palmistry Report"}</div>
        <div className="text-sm text-slate-400">{lang === "hi" ? "PDF · ईमेल · WhatsApp डिलीवरी" : "PDF · Email · WhatsApp delivery"}</div>
      </div>
      <div className="text-right">
        <div className="flex items-center justify-end text-3xl font-serif text-amber-300" data-testid="service-price"><IndianRupee size={22} />{price ?? "51"}</div>
        <div className="text-xs text-slate-400">{lang === "hi" ? "प्रति रिपोर्ट" : "per report"}</div>
      </div>
      <Link to="/upload" className="btn-gold rounded-full px-5 py-2.5 text-sm text-center" data-testid="service-cta">{lang === "hi" ? "रिपोर्ट पाएँ" : "Get report"}</Link>
    </div>
  );
}

export default function PolicyPage({ slug }) {
  const { t, lang } = useApp();
  const p = POLICIES[slug];
  useEffect(() => {
    document.title = `${p.title.en} | AstroFriend`;
    window.scrollTo(0, 0);
  }, [slug]); // eslint-disable-line
  const L = lang === "hi" ? "hi" : "en";

  return (
    <div className="mx-auto max-w-2xl px-6 py-14" data-testid={`policy-page-${slug}`}>
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-amber-300/80 hover:text-amber-200 mb-6">
        <ArrowLeft size={16} /> {t.common.back}
      </Link>
      <h1 className="font-serif text-3xl sm:text-4xl font-bold text-amber-50 mb-3" data-testid="policy-title">{p.title[L]}</h1>
      <p className="text-slate-300 mb-8">{p.intro[L]}</p>

      {p.contactCard && <ContactCard />}
      {p.serviceCard && <ServiceCard lang={L} />}

      <div className="mt-8 space-y-6">
        {p.sections.map((s, i) => (
          <section key={i} className="glass rounded-2xl p-5 border border-white/10" data-testid={`policy-section-${i}`}>
            {s.h && <h2 className="text-amber-200 font-medium mb-2">{s.h[L]}</h2>}
            <p className="text-slate-200 leading-relaxed">{s[L]}</p>
          </section>
        ))}
      </div>

      <p className="mt-8 text-xs text-slate-500">
        {L === "hi" ? "प्रश्नों के लिए: " : "Questions? "}
        <Link to="/contact" className="text-amber-300 hover:underline">{L === "hi" ? "संपर्क करें" : "Contact us"}</Link>
      </p>
    </div>
  );
}
